/* eslint-disable no-nested-ternary -- stfu */
import { Badge } from '@eppendorf/vnls-react-components';
import cs from 'classnames';
import { detailedDiff } from 'deep-object-diff';
import React, { useMemo } from 'react';

import styles from './diff-view.module.scss';

// ==================================================================================
// TYPE DEFINITIONS
// Defines the structure for the differences found between the two objects.
// ==================================================================================

/** Represents a single added or deleted property */
interface DiffEntry {
  path: (string | number)[];
  pathLabel?: string;
  value: unknown;
}

/** Represents a single updated property, holding both old and new values */
interface DiffUpdateEntry {
  path: (string | number)[];
  oldValue: unknown;
  newValue: unknown;
}

/** The final result object containing categorized lists of all changes */
export interface DiffResult {
  added: DiffEntry[];
  deleted: DiffEntry[];
  updated: DiffUpdateEntry[];
}

// ==================================================================================
// DIFF CALCULATION LOGIC
// ==================================================================================

/**
 * Default blocklist of property keys to exclude from diff display.
 * These properties will be filtered out and not shown to users.
 */
const DEFAULT_BLOCKLIST = [
  'updatedAt',
  'createdAt',
  'lastModified',
  'timestamp',
  'devicePushTokens',
  'deviceInfoSentFromDeviceAt',
  'acceptedTermsAndConditionsAt',
  'updatedBy',
];

/**
 * Checks if a path should be filtered out based on the blocklist.
 * @param path The property path to check
 * @param blocklist Array of property names to exclude
 * @returns true if the path should be filtered out
 */
function shouldFilterPath(path: (string | number)[], blocklist: string[]): boolean {
  // Check if any part of the path matches a blocklisted property
  return path.some(
    (segment) => typeof segment === 'string' && blocklist.includes(segment),
  );
}

/**
 * Recursively flattens a nested object from deep-object-diff into a flat array of entries.
 * @param obj The nested object from deep-object-diff
 * @param basePath The current path from the root
 * @param blocklist Array of property names to exclude from the result
 * @returns An array of flattened entries with paths and values
 */
function flattenDiffObject(
  obj: Record<string, unknown>,
  basePath: (string | number)[] = [],
  blocklist: string[] = DEFAULT_BLOCKLIST,
): DiffEntry[] {
  const result: DiffEntry[] = [];

  const traverse = (current: unknown, path: (string | number)[]) => {
    // Check if this path should be filtered out
    if (shouldFilterPath(path, blocklist)) {
      return;
    }

    if (current === null || current === undefined) {
      if (path.length > 0) {
        result.push({ path, value: current });
      }
      return;
    }

    if (Array.isArray(current)) {
      // Handle arrays - iterate through indices
      current.forEach((item, index) => {
        traverse(item, [...path, index]);
      });
    } else if (typeof current === 'object') {
      const currentObj = current as Record<string, unknown>;

      // Check if this represents a complete object added to an array
      // Look for numeric string keys (like "3") that indicate array indices
      const lastPathElement = path[path.length - 1];
      const isArrayIndex =
        typeof lastPathElement === 'string' && /^\d+$/.test(lastPathElement);

      if (isArrayIndex) {
        // Check if this object has multiple primitive properties (likely a complete object)
        const propertyCount = Object.keys(currentObj).length;
        const allPrimitiveValues = Object.values(currentObj).every(
          (value) => typeof value !== 'object' || value === null,
        );

        // If it's an array element with multiple primitive properties,
        // treat it as a single entity (complete object added to array)
        if (propertyCount > 1 && allPrimitiveValues) {
          result.push({ path, value: currentObj });
          return;
        }
      }

      // Check if this is a leaf node (all values are primitives, null, undefined, or empty)
      const isLeafNode = Object.values(currentObj).every(
        (value) =>
          value === undefined ||
          value === null ||
          typeof value !== 'object' ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'object' &&
            Object.keys(value as Record<string, unknown>).length === 0),
      );

      if (isLeafNode) {
        // This is a leaf node, add entries for each key-value pair
        Object.entries(currentObj).forEach(([key, value]) => {
          result.push({ path: [...path, key], value });
        });
      } else {
        // Continue traversing
        Object.entries(currentObj).forEach(([key, value]) => {
          traverse(value, [...path, key]);
        });
      }
    } else if (path.length > 0) {
      // Primitive value
      result.push({ path, value: current });
    }
  };

  traverse(obj, basePath);
  return result;
}

/**
 * Gets a value from an object at the specified path.
 * @param obj The object to traverse
 * @param path The path to the value
 * @returns The value at the specified path
 */
function getValueAtPath(obj: unknown, path: (string | number)[]): unknown {
  return path.reduce((current, key) => {
    if (current === null || current === undefined) return undefined;
    // eslint-disable-next-line security/detect-object-injection -- safe as we control the path
    return (current as Record<string, unknown>)[key];
  }, obj);
}

/**
 * Calculates differences between two objects using deep-object-diff library
 * and transforms the result to match our expected format.
 * @param lhs The "before" object
 * @param rhs The "after" object
 * @param blocklist Optional array of property names to exclude from the diff
 * @returns DiffResult object with added, deleted, and updated arrays
 */
function calculateDiff(
  lhs: object,
  rhs: object,
  blocklist: string[] = DEFAULT_BLOCKLIST,
): DiffResult {
  const diff = detailedDiff(lhs, rhs);

  const added = flattenDiffObject(diff.added as Record<string, unknown>, [], blocklist);
  const deleted = flattenDiffObject(
    diff.deleted as Record<string, unknown>,
    [],
    blocklist,
  ).map((entry) => ({
    path: entry.path,
    value: getValueAtPath(lhs, entry.path), // Get the original value from lhs
  }));
  const updated = flattenDiffObject(
    diff.updated as Record<string, unknown>,
    [],
    blocklist,
  ).map((entry) => ({
    path: entry.path,
    oldValue: getValueAtPath(lhs, entry.path),
    newValue: entry.value,
  }));

  return {
    added: added.filter((entry) => !shouldFilterPath(entry.path, blocklist)),
    deleted: deleted.filter((entry) => !shouldFilterPath(entry.path, blocklist)),
    updated: updated.filter((entry) => !shouldFilterPath(entry.path, blocklist)),
  };
}

// ==================================================================================
// REACT COMPONENTS
// ==================================================================================

/**
 * Converts a string to title case, handling camelCase and snake_case.
 * e.g., 'phoneNumber' becomes 'Phone Number'
 * e.g., 'user_name' becomes 'User Name'
 */
const toTitleCase = (str: string): string =>
  str
    // Handle camelCase: insert space before uppercase letters
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Handle snake_case: replace underscores with spaces
    .replace(/_/g, ' ')
    // Handle kebab-case: replace hyphens with spaces
    .replace(/-/g, ' ')
    // Capitalize first letter of each word
    .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Formats a value for display in a human-readable way.
 */
const formatValue = (value: unknown): string => {
  if (value === undefined) return '-';
  if (value === null) return '-';
  if (typeof value === 'string') return `${value?.length > 0 ? value : '-'}`;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  // Handle objects in a more human-readable way
  if (typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    const entries = Object.entries(obj);

    if (entries.length === 0) return '-';

    // Format as human-readable properties
    const formattedEntries = entries.map(([key, val]) => {
      if (DEFAULT_BLOCKLIST.includes(key)) {
        return undefined;
      }
      let formattedValue: string;
      if (typeof val === 'string') {
        formattedValue = `${val}`;
      } else if (val === null || val === undefined) {
        formattedValue = String(val);
      } else if (typeof val === 'object') {
        // Recursively format nested objects, but keep them concise
        formattedValue = formatValue(val);
      } else {
        formattedValue = String(val);
      }
      return `${toTitleCase(key)}: ${formattedValue}`;
    });

    // Always display object properties vertically for better readability
    return formattedEntries.filter(Boolean).join('\n');
  }

  // Handle arrays
  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    const formattedItems = value.map((item) => formatValue(item));

    // For simple arrays, show inline without brackets
    if (value.length <= 3 && formattedItems.every((item) => item.length < 20)) {
      return formattedItems.join(', ');
    }

    // For complex arrays, show with line breaks
    return formattedItems.join('\n');
  }

  // Fallback to JSON stringify for other cases
  return JSON.stringify(value, null, 2);
};

/**
 * Joins a path array into a human-readable string, making array indices more user-friendly.
 * e.g., ['devices', '3'] becomes "Devices → #4"
 * e.g., ['recipients', '2'] becomes "Recipients → #3"
 * e.g., ['phoneNumber', '0'] becomes "Phone Number → #1"
 */
const formatPath = (path: (string | number)[]): string =>
  path
    .map((segment) => {
      // Check if this segment is a numeric array index
      if (typeof segment === 'string' && /^\d+$/.test(segment)) {
        const arrayIndex = parseInt(segment, 10);
        // Use 1-based numbering instead of 0-based for user-friendliness
        return `#${arrayIndex + 1}`;
      }

      // Convert string segments to title case
      return toTitleCase(String(segment));
    })
    .join(' → ');

/**
 * A component to render a single row in the diff report.
 */
const DiffRow: React.FC<{
  type: 'added' | 'deleted' | 'updated';
  entry: DiffEntry | DiffUpdateEntry;
}> = React.memo(({ type, entry }) => {
  const pathString =
    ('pathLabel' in entry ? entry.pathLabel : undefined) ?? formatPath(entry.path);

  const valueBefore =
    type === 'updated'
      ? formatValue((entry as DiffUpdateEntry).oldValue)
      : type === 'deleted'
        ? formatValue((entry as DiffEntry).value)
        : '-';
  const valueAfter =
    type === 'updated'
      ? formatValue((entry as DiffUpdateEntry).newValue)
      : type === 'added'
        ? formatValue((entry as DiffEntry).value)
        : '-';

  if (valueBefore === '-' && valueAfter === '-') {
    return null;
  }

  const badge =
    type === 'added' ? (
      <Badge color="green" size="small">
        Added
      </Badge>
    ) : type === 'deleted' ? (
      <Badge color="red" size="small">
        Deleted
      </Badge>
    ) : type === 'updated' ? (
      <Badge color="blue" size="small">
        Updated
      </Badge>
    ) : null;

  return (
    <div className={styles.diffRow}>
      <div className={styles.badgeCell}>{badge}</div>
      <div className={styles.pathCell}>{pathString}</div>
      <div className={styles.valueCell}>
        <span
          className={cs('p-s', {
            [styles.bgRed]: valueBefore !== '-',
          })}
        >
          {valueBefore}
        </span>
      </div>
      <div className={styles.valueCell}>
        <span
          className={cs('p-s', {
            [styles.bgGreen]: valueAfter !== '-',
          })}
        >
          {valueAfter}
        </span>
      </div>
    </div>
  );
});

/**
 * A section for displaying a specific type of change (Added, Deleted, or Updated).
 */
function DiffSection({
  added,
  deleted,
  updated,
  settingLabel = 'Setting',
}: {
  added: DiffEntry[];
  deleted: DiffEntry[];
  updated: DiffUpdateEntry[];
  settingLabel?: string;
}) {
  return (
    <div className="flex flex__dir--column w-full overflow-hidden">
      <div className={styles.diffHeaderRow}>
        <div className={styles.badgeCell}>Change</div>
        <div className={styles.pathCell}>{settingLabel}</div>
        <div className={styles.valueCell}>Previous value</div>
        <div className={styles.valueCell}>New value</div>
      </div>
      {added.map((entry) => (
        <DiffRow key={`added-${entry.path.join('-')}`} type="added" entry={entry} />
      ))}
      {deleted.map((entry) => (
        <DiffRow key={`deleted-${entry.path.join('-')}`} type="deleted" entry={entry} />
      ))}
      {updated.map((entry) => (
        <DiffRow key={`updated-${entry.path.join('-')}`} type="updated" entry={entry} />
      ))}
    </div>
  );
}

/**
 * The main component that orchestrates the diffing and rendering.
 * It takes a "before" (lhs) and "after" (rhs) object as props.
 */
interface ObjectDiffViewProps {
  lhs?: unknown;
  rhs?: unknown;
  /** Optional array of property names to exclude from the diff display */
  blocklist?: string[];
  additionalChanges?: Pick<DiffResult, 'added' | 'deleted'>;
  additionalChangesColumnLabel?: string;
}

export function ObjectDiffView({
  lhs,
  rhs,
  blocklist = DEFAULT_BLOCKLIST,
  additionalChanges,
  additionalChangesColumnLabel,
}: ObjectDiffViewProps) {
  const diffResult = useMemo(() => {
    if (!lhs || !rhs) {
      return { added: [], deleted: [], updated: [] };
    }
    return calculateDiff(lhs, rhs, blocklist);
  }, [lhs, rhs, blocklist]);

  const hasSettingChanges =
    diffResult.added.length > 0 ||
    diffResult.deleted.length > 0 ||
    diffResult.updated.length > 0;

  const added = [...diffResult.added, ...(additionalChanges?.added ?? [])];
  const deleted = [...diffResult.deleted, ...(additionalChanges?.deleted ?? [])];

  if (!hasSettingChanges && !added.length && !deleted.length) {
    return <div>No changes</div>;
  }

  return (
    <DiffSection
      added={added}
      deleted={deleted}
      updated={diffResult.updated}
      settingLabel={hasSettingChanges ? undefined : additionalChangesColumnLabel}
    />
  );
}
/* eslint-enable no-nested-ternary -- stfu */
